import React, { useState, useEffect, useCallback } from 'react';
import { Apple, ChevronLeft, ChevronRight, Trash2, Plus, ChevronUp, ChevronDown, Pencil, X } from 'lucide-react';
import {
  getPlayers, getNutritionDiary, saveNutritionGoals,
  getNutritionProfileReports, addNutritionProfileReport, updateNutritionProfileReport, deleteNutritionProfileReport,
} from '../api';
import { useUrlParam } from '../hooks/useUrlParam';
import ChefWeekPlanner from '../components/ChefWeekPlanner';

// Own copy of the meal list (same convention as PlayerNutrition.js).
const MEALS = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch',     label: 'Lunch' },
  { id: 'dinner',    label: 'Dinner' },
  { id: 'snack',     label: 'Snacks' },
];

const EMPTY = {
  entries: [],
  totals: { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, saturated_fat_g: 0, unsaturated_fat_g: 0 },
  goal: null,
  effectiveGoals: {
    kcal_goal: null, protein_g_goal: null, carbs_g_goal: null, fat_g_goal: null,
    protein_is_manual: false, carbs_is_manual: false,
  },
  autoGoals: null,
};

const tod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
function shiftDate(str, delta) {
  const d = new Date(str + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDay(str) {
  return new Date(str + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long' });
}
function fmtShort(d) {
  return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—';
}

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: 12 };
const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const tag = (kind) => ({
  fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
  padding: '1px 5px', borderRadius: 4, marginLeft: 6,
  color: kind === 'auto' ? '#3b82f6' : 'var(--text-muted)',
  border: `1px solid ${kind === 'auto' ? 'rgba(59,130,246,0.4)' : 'var(--border-strong)'}`,
});

// Heuristic "on track" band — within 90–110 % of goal reads as on target; outside is flagged.
// Deliberately simple for v1; adjust the band here if the staff want it tighter/looser.
function statusFor(value, goal) {
  if (!goal) return { pct: null, label: '—', color: 'var(--text-dimmed)' };
  const pct = Math.round((value / goal) * 100);
  if (pct >= 90 && pct <= 110) return { pct, label: 'on track', color: '#10b981' };
  return { pct, label: pct < 90 ? 'under goal' : 'over goal', color: '#f59e0b' };
}

function TotalRow({ label, source, value, goal, unit }) {
  const s = statusFor(value, goal);
  const width = s.pct == null ? 0 : Math.min(100, s.pct);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-secondary)' }}>
          {label}<span style={tag(source)}>{source}</span>
        </span>
        <span style={{ color: 'var(--text-muted)' }}>
          {value}{unit}{goal != null ? ` / ${goal}${unit}` : ''}
          {s.pct != null && <span style={{ color: s.color, fontWeight: 600 }}>{`  ·  ${s.pct}% (${s.label})`}</span>}
        </span>
      </div>
      {goal != null && (
        <div style={{ height: 6, background: 'var(--gauge-track)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${width}%`, background: s.color, borderRadius: 99 }} />
        </div>
      )}
    </div>
  );
}

// ── Read-only day breakdown (per meal, per food) ────────────────────────────
function DayBreakdown({ entries }) {
  if (!entries.length) {
    return <div style={{ fontSize: 13, color: 'var(--text-dimmed)', padding: '8px 0' }}>Nothing logged for this day.</div>;
  }
  return (
    <div>
      {MEALS.map(m => {
        const rows = entries.filter(e => e.meal_type === m.id);
        if (!rows.length) return null;
        const kcal = rows.reduce((s, e) => s + e.kcal, 0);
        return (
          <div key={m.id} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              <span>{m.label}</span><span>{kcal} kcal</span>
            </div>
            {rows.map(e => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 4, fontSize: 12 }}>
                <span style={{ color: 'var(--text-primary)' }}>
                  {e.food_name}
                  {e.food_source === 'off' && <span style={tag('auto')}>barcode</span>}
                  <span style={{ color: 'var(--text-dimmed)' }}>{'  '}{e.amount_grams} g</span>
                </span>
                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {e.kcal} kcal · P {e.protein_g} / C {e.carbs_g} / F {e.fat_g} (verz {e.saturated_fat_g} / onverz {e.unsaturated_fat_g})
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── The auto protein/carb calculation, with its derivation — the suggestion the goals editor
// pre-fills and that applies whenever a macro is left on "Auto". ─────────────
function AutoGoalsPanel({ auto }) {
  const line = { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0' };
  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
        Auto calculation <span style={tag('auto')}>suggestion</span>
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 10px' }}>
        From body weight and the match-day type. Used for any macro left on “Auto” above.
      </p>

      {!auto || auto.weight_kg == null ? (
        <div style={{ fontSize: 12, color: '#f59e0b' }}>
          No body weight on record for this player, so protein &amp; carb goals can’t be
          calculated. Add a weight in the player profile / weekly weigh-in.
        </div>
      ) : (
        <>
          <div style={line}>
            <span style={{ color: 'var(--text-secondary)' }}>Protein</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{auto.protein_g} g</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 6 }}>
            {auto.protein_g_per_kg} g/kg × {auto.weight_kg} kg — every day
          </div>
          <div style={line}>
            <span style={{ color: 'var(--text-secondary)' }}>Carbs</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{auto.carbs_g} g</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>
            {auto.carb_g_per_kg} g/kg × {auto.weight_kg} kg —{' '}
            {auto.carb_basis === 'md'
              ? `${auto.md_label} in the week schedule`
              : 'no match-day type for this date, using the default band'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            Weight: {auto.weight_kg} kg
            {auto.weight_source ? ` (${auto.weight_source}${auto.weight_date ? `, ${fmtShort(auto.weight_date)}` : ''})` : ''}
          </div>
        </>
      )}
    </div>
  );
}

// ── Goals editor ──────────────────────────────────────────────────────────
// Calories & fat are always manual. Protein & carbs default to the auto calc (shown as the
// suggestion) but can be switched to Manual and overridden.

// Module-level so they aren't re-created every GoalsEditor render (which would drop input focus).
function NumField({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</label>
      <input type="number" min="0" step="1" value={value}
        onChange={e => onChange(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
    </div>
  );
}

function AutoManualRow({ label, suggestion, mode, value, onMode, onValue }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button style={{ ...btn(mode === 'auto'), padding: '3px 9px' }} onClick={() => onMode('auto')}>Auto</button>
          <button style={{ ...btn(mode === 'manual'), padding: '3px 9px' }} onClick={() => onMode('manual')}>Manual</button>
        </div>
      </div>
      {mode === 'auto' ? (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Using auto: <strong>{suggestion != null ? `${suggestion} g` : 'no weight on record'}</strong>
        </div>
      ) : (
        <input type="number" min="0" step="1" value={value}
          onChange={e => onValue(e.target.value)} style={{ ...inputStyle, width: '100%' }}
          placeholder={suggestion != null ? `auto suggestion: ${suggestion}` : ''} />
      )}
    </div>
  );
}

function GoalsEditor({ playerId, goal, auto, onSaved, user }) {
  const init = () => ({
    kcal_goal: goal?.kcal_goal ?? '',
    fat_g_goal: goal?.fat_g_goal ?? '',
    protein_mode: goal?.protein_is_manual ? 'manual' : 'auto',
    carbs_mode: goal?.carbs_is_manual ? 'manual' : 'auto',
    // prefill the manual input with the current override, else the auto suggestion
    protein_g_goal: goal?.protein_is_manual ? (goal.protein_g_goal ?? '') : (auto?.protein_g ?? ''),
    carbs_g_goal: goal?.carbs_is_manual ? (goal.carbs_g_goal ?? '') : (auto?.carbs_g ?? ''),
  });
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Re-seed the form whenever the player or the goal/auto data changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setForm(init()); setMsg(null); }, [playerId, goal, auto]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const numOk = v => v !== '' && Number(v) >= 0;

  const save = async () => {
    if (!numOk(form.kcal_goal)) return setMsg({ ok: false, text: 'Calories: enter a number of 0 or higher' });
    if (!numOk(form.fat_g_goal)) return setMsg({ ok: false, text: 'Fat: enter a number of 0 or higher' });
    if (form.protein_mode === 'manual' && !numOk(form.protein_g_goal)) return setMsg({ ok: false, text: 'Protein: enter a number of 0 or higher' });
    if (form.carbs_mode === 'manual' && !numOk(form.carbs_g_goal)) return setMsg({ ok: false, text: 'Carbs: enter a number of 0 or higher' });

    setSaving(true); setMsg(null);
    try {
      await saveNutritionGoals({
        player_id: playerId,
        updated_by: user?.id,
        kcal_goal: Number(form.kcal_goal),
        fat_g_goal: Number(form.fat_g_goal),
        protein_is_manual: form.protein_mode === 'manual',
        protein_g_goal: form.protein_mode === 'manual' ? Number(form.protein_g_goal) : null,
        carbs_is_manual: form.carbs_mode === 'manual',
        carbs_g_goal: form.carbs_mode === 'manual' ? Number(form.carbs_g_goal) : null,
      });
      setMsg({ ok: true, text: 'Goals saved.' });
      onSaved();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Daily goals</div>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 12px' }}>
        Calories &amp; fat are set by staff. Protein &amp; carbs default to the auto calculation
        (from body weight + match-day type) — switch to Manual to override.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <NumField label="Calories (kcal)" value={form.kcal_goal} onChange={v => set('kcal_goal', v)} />
        <NumField label="Fat (g)" value={form.fat_g_goal} onChange={v => set('fat_g_goal', v)} />
      </div>
      <AutoManualRow label="Protein (g)" suggestion={auto?.protein_g} mode={form.protein_mode}
        value={form.protein_g_goal} onMode={m => set('protein_mode', m)} onValue={v => set('protein_g_goal', v)} />
      <AutoManualRow label="Carbs (g)" suggestion={auto?.carbs_g} mode={form.carbs_mode}
        value={form.carbs_g_goal} onMode={m => set('carbs_mode', m)} onValue={v => set('carbs_g_goal', v)} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
        <button style={btn(true)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save goals'}</button>
        {msg && <span style={{ fontSize: 12, fontWeight: 600, color: msg.ok ? '#10b981' : '#ef4444' }}>{msg.text}</span>}
      </div>
      {goal?.updated_at && (
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8 }}>
          Last updated: {new Date(goal.updated_at).toLocaleDateString('en-GB')}
        </div>
      )}
    </div>
  );
}

// ── Player profile: Doel / Voedingsprofiel / Actieplan ──────────────────────
// One row per player, edited in place and saved as a whole (unlike NotesPanel below, which is
// an append-only conversation log). The Actieplan section is also shown, read-only, on the
// player's own Food Tracker screen.
const GOAL_OPTIONS = [
  { key: 'performance', label: 'Prestaties verbeteren' },
  { key: 'muscle', label: 'Spiermassa' },
  { key: 'fatloss', label: 'Afvallen/Vetverlies' },
  { key: 'recovery', label: 'Herstel verbeteren' },
  { key: 'energy', label: 'Meer energie' },
  { key: 'other', label: 'Anders' },
];
// Per-component timing label relative to training — NOT one colour for the whole profile:
// every meal and every individual snack picks its own (or none).
const PROFILE_COLORS = [
  { key: 'orange', label: 'Voor het sporten', hex: '#f59e0b' },
  { key: 'red', label: 'Tijdens het sporten', hex: '#ef4444' },
  { key: 'blue', label: 'Na het sporten', hex: '#3b82f6' },
];

function ColorDotPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {PROFILE_COLORS.map(c => (
        <button key={c.key} type="button" title={c.label} onClick={() => onChange(value === c.key ? null : c.key)}
          style={{
            width: 18, height: 18, borderRadius: '50%', cursor: 'pointer', padding: 0,
            background: c.hex, border: value === c.key ? '2px solid var(--text-primary)' : '2px solid transparent',
            opacity: value && value !== c.key ? 0.35 : 1,
          }} />
      ))}
    </div>
  );
}

function MealBlockEditor({ label, value, onChange }) {
  const v = value || { text: '', color: null };
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</span>
        <ColorDotPicker value={v.color} onChange={color => onChange({ text: v.text, color })} />
      </div>
      <textarea value={v.text || ''} onChange={e => onChange({ color: v.color, text: e.target.value })}
        placeholder={`${label}…`} style={{ ...inputStyle, width: '100%', minHeight: 40, resize: 'vertical' }} />
    </div>
  );
}

function SnackListEditor({ snacks, onChange }) {
  const update = (i, patch) => onChange(snacks.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const remove = (i) => onChange(snacks.filter((_, idx) => idx !== i));
  const add = () => onChange([...snacks, { text: '', time: '', color: null }]);
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Tussendoortjes</div>
      {snacks.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, background: 'var(--bg-app)', borderRadius: 8, padding: 8 }}>
          <input type="time" value={s.time || ''} onChange={e => update(i, { time: e.target.value })} style={{ ...inputStyle, width: 88 }} />
          <input value={s.text || ''} onChange={e => update(i, { text: e.target.value })} placeholder="Tussendoortje…" style={{ ...inputStyle, flex: 1 }} />
          <ColorDotPicker value={s.color} onChange={color => update(i, { color })} />
          <button type="button" onClick={() => remove(i)} style={{ ...btn(false, '#ef4444'), padding: '4px 7px' }}><Trash2 size={12} /></button>
        </div>
      ))}
      <button type="button" onClick={add} style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 4 }}>
        <Plus size={12} /> Tussendoortje toevoegen
      </button>
    </div>
  );
}

function ActionPlanEditor({ items, onChange }) {
  const update = (i, text) => onChange(items.map((x, idx) => idx === i ? text : x));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, '']);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div>
      {items.map((text, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 18 }}>{i + 1}.</span>
          <input value={text} onChange={e => update(i, e.target.value)} placeholder="Actiepunt…" style={{ ...inputStyle, flex: 1 }} />
          <button type="button" onClick={() => move(i, -1)} disabled={i === 0} style={{ ...btn(false), padding: '4px 6px' }}><ChevronUp size={12} /></button>
          <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} style={{ ...btn(false), padding: '4px 6px' }}><ChevronDown size={12} /></button>
          <button type="button" onClick={() => remove(i)} style={{ ...btn(false, '#ef4444'), padding: '4px 7px' }}><Trash2 size={12} /></button>
        </div>
      ))}
      <button type="button" onClick={add} style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 4 }}>
        <Plus size={12} /> Punt toevoegen
      </button>
    </div>
  );
}

const EMPTY_PROFILE_FORM = { note: '', goals: { selected: [], other_text: '' }, nutrition_profile: { breakfast: null, lunch: null, dinner: null, snacks: [] }, action_plan: [] };

// Read-only rendering of one saved report — used for every entry in the history list below.
function ProfileReportSummary({ report }) {
  const g = report.goals || { selected: [], other_text: '' };
  const np = report.nutrition_profile || {};
  const ap = report.action_plan || [];
  const goalLabels = GOAL_OPTIONS.filter(o => (g.selected || []).includes(o.key)).map(o => o.label);
  const dot = (color) => color && <span style={{ width: 7, height: 7, borderRadius: '50%', background: PROFILE_COLORS.find(c => c.key === color)?.hex, display: 'inline-block', marginRight: 5 }} />;
  return (
    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
      {report.note && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 3 }}>Notitie</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{report.note}</div>
        </div>
      )}
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontWeight: 700 }}>Doel: </span>
        {goalLabels.length || g.other_text ? [...goalLabels, g.other_text].filter(Boolean).join(', ') : <span style={{ color: 'var(--text-dimmed)' }}>—</span>}
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 700, marginBottom: 3 }}>Voedingsprofiel</div>
        {['breakfast', 'lunch', 'dinner'].map(slot => np[slot]?.text && (
          <div key={slot}>{dot(np[slot].color)}{{ breakfast: 'Ontbijt', lunch: 'Lunch', dinner: 'Avondeten' }[slot]}: {np[slot].text}</div>
        ))}
        {(np.snacks || []).map((s, i) => s.text && (
          <div key={i}>{dot(s.color)}Tussendoortje{s.time ? ` (${s.time})` : ''}: {s.text}</div>
        ))}
        {!np.breakfast?.text && !np.lunch?.text && !np.dinner?.text && !(np.snacks || []).some(s => s.text) && <span style={{ color: 'var(--text-dimmed)' }}>—</span>}
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 3 }}>Actieplan</div>
        {ap.filter(Boolean).length
          ? <ol style={{ margin: 0, paddingLeft: 16 }}>{ap.filter(Boolean).map((t, i) => <li key={i}>{t}</li>)}</ol>
          : <span style={{ color: 'var(--text-dimmed)' }}>—</span>}
      </div>
    </div>
  );
}

function NutritionProfilePanel({ playerId, user }) {
  const isAdmin = user?.role === 'admin'; // same requesterRole convention checked server-side
  const [reports, setReports] = useState(null); // full history, newest first
  const [form, setForm] = useState(EMPTY_PROFILE_FORM);
  const [editingId, setEditingId] = useState(null); // report id being edited, or null = new report
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [expanded, setExpanded] = useState(null); // id of the expanded history entry, if any

  const load = useCallback(() => {
    setReports(null);
    setEditingId(null);
    getNutritionProfileReports(playerId).then(list => {
      setReports(list || []);
      // Prefill a new report from the most recent one — a convenience for staff (most fields
      // carry over unchanged most weeks) that has no bearing on saving: "Save" always INSERTS
      // a new dated row, it never touches the report this was prefilled from.
      const latest = (list && list[0]) || null;
      // Note is deliberately NOT carried over into the prefill — unlike Doel/Voedingsprofiel/
      // Actieplan (which mostly repeat week to week), a conversation note is specific to the
      // moment it was written; starting the new report with last time's note text would very
      // likely lead to it being left in place by mistake.
      setForm(latest ? { note: '', goals: latest.goals, nutrition_profile: latest.nutrition_profile, action_plan: latest.action_plan } : EMPTY_PROFILE_FORM);
    }).catch(() => { setReports([]); setForm(EMPTY_PROFILE_FORM); });
  }, [playerId]);
  useEffect(() => { load(); }, [load]);

  if (!reports) return <div style={card}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div></div>;

  const setGoals = (patch) => setForm(p => ({ ...p, goals: { ...p.goals, ...patch } }));
  const toggleGoal = (key) => setGoals({
    selected: form.goals.selected.includes(key)
      ? form.goals.selected.filter(k => k !== key)
      : [...form.goals.selected, key],
  });
  const setNP = (patch) => setForm(p => ({ ...p, nutrition_profile: { ...p.nutrition_profile, ...patch } }));

  // Loads an existing report into the same form used for a new one — Save then PUTs it instead
  // of POSTing a new row. Admin-only in the UI below; also re-checked server-side.
  const startEdit = (report) => {
    setEditingId(report.id);
    setForm({ note: report.note || '', goals: report.goals, nutrition_profile: report.nutrition_profile, action_plan: report.action_plan });
    setMsg(null);
    setExpanded(null);
  };
  const cancelEdit = () => {
    setEditingId(null);
    const latest = reports[0] || null;
    setForm(latest ? { note: '', goals: latest.goals, nutrition_profile: latest.nutrition_profile, action_plan: latest.action_plan } : EMPTY_PROFILE_FORM);
    setMsg(null);
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      if (editingId) {
        await updateNutritionProfileReport(editingId, { updated_by: user?.id, ...form }, user?.role);
        setMsg({ ok: true, text: 'Report updated.' });
      } else {
        await addNutritionProfileReport({ player_id: playerId, created_by: user?.id, ...form });
        setMsg({ ok: true, text: 'New report saved.' });
      }
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Save failed' });
    } finally { setSaving(false); }
  };
  const remove = async (id) => { await deleteNutritionProfileReport(id); load(); };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{editingId ? 'Editing report' : 'New report'}</span>
        {editingId && (
          <button type="button" onClick={cancelEdit} style={{ ...btn(false), padding: '3px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={11} /> Cancel edit
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 12 }}>
        {editingId
          ? 'Editing a past report in place — this changes that dated entry, it does not create a new one.'
          : 'Prefilled from the most recent report — saving always adds a new dated entry, it never changes past ones.'}
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Notitie</div>
      <textarea value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
        placeholder="Notitie van dit gesprek…" style={{ ...inputStyle, width: '100%', minHeight: 60, resize: 'vertical' }} />

      <div style={{ fontSize: 13, fontWeight: 700, margin: '20px 0 10px' }}>Doel</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {GOAL_OPTIONS.map(o => (
          <label key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.goals.selected.includes(o.key)} onChange={() => toggleGoal(o.key)} />
            {o.label}
          </label>
        ))}
      </div>
      {form.goals.selected.includes('other') && (
        <input value={form.goals.other_text} onChange={e => setGoals({ other_text: e.target.value })}
          placeholder="Anders…" style={{ ...inputStyle, width: '100%', marginTop: 8 }} />
      )}

      <div style={{ fontSize: 13, fontWeight: 700, margin: '20px 0 10px' }}>Voedingsprofiel</div>
      <MealBlockEditor label="Ontbijt" value={form.nutrition_profile.breakfast} onChange={v => setNP({ breakfast: v })} />
      <MealBlockEditor label="Lunch" value={form.nutrition_profile.lunch} onChange={v => setNP({ lunch: v })} />
      <MealBlockEditor label="Avondeten" value={form.nutrition_profile.dinner} onChange={v => setNP({ dinner: v })} />
      <SnackListEditor snacks={form.nutrition_profile.snacks} onChange={snacks => setNP({ snacks })} />
      <div style={{ display: 'flex', gap: 14, fontSize: 10, color: 'var(--text-dimmed)', marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {PROFILE_COLORS.map(c => (
          <span key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.hex, display: 'inline-block' }} />
            {c.label}
          </span>
        ))}
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, margin: '20px 0 10px' }}>Actieplan</div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 8 }}>Het meest recente verslag is zichtbaar voor de speler zelf (alleen-lezen).</div>
      <ActionPlanEditor items={form.action_plan} onChange={action_plan => setForm(p => ({ ...p, action_plan }))} />

      <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
        <button style={btn(true)} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : editingId ? 'Save changes' : 'Save as new report'}
        </button>
        {msg && <span style={{ fontSize: 12, color: msg.ok ? '#10b981' : '#ef4444' }}>{msg.text}</span>}
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, margin: '24px 0 10px', paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        Previous reports {reports.length > 0 && <span style={{ color: 'var(--text-dimmed)', fontWeight: 400 }}>({reports.length})</span>}
      </div>
      {reports.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>No reports yet.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {reports.map(r => (
          <div key={r.id} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <button type="button" onClick={() => setExpanded(x => x === r.id ? null : r.id)}
                style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'inherit', textAlign: 'left' }}>
                {new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {r.author ? ` · ${r.author}` : ''} {expanded === r.id ? '▲' : '▼'}
              </button>
              <div style={{ display: 'flex', gap: 4 }}>
                {isAdmin && (
                  <button style={{ ...btn(false), padding: '2px 6px' }} onClick={() => startEdit(r)} title="Edit"><Pencil size={11} /></button>
                )}
                <button style={{ ...btn(false, '#ef4444'), padding: '2px 6px' }} onClick={() => remove(r.id)} title="Delete"><Trash2 size={11} /></button>
              </div>
            </div>
            {r.updated_at && (
              <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 3 }}>
                Bewerkt op {new Date(r.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {r.editor ? ` · ${r.editor}` : ''}
              </div>
            )}
            {expanded === r.id && <div style={{ marginTop: 8 }}><ProfileReportSummary report={r} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// The existing admin view — player diary overview + Doel/Voedingsprofiel/Actieplan reports.
// Unchanged in substance; only renamed and un-exported so the new Nutrition() below can put it
// behind the admin's "Spelers"/"Chef" toggle instead of it being the page's only content.
function PlayersNutritionView({ user, toggle }) {
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useUrlParam('player_id', '');
  const [date, setDate] = useUrlParam('date', tod());
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getPlayers()
      .then(p => {
        const active = (p || []).filter(pl => pl.active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setPlayers(active);
        if (!playerId && active.length) setPlayerId(String(active[0].id));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!playerId || !date) return;
    setLoading(true);
    try { setData({ ...EMPTY, ...(await getNutritionDiary(playerId, date)) }); }
    catch { setData(EMPTY); }
    finally { setLoading(false); }
  }, [playerId, date]);

  useEffect(() => { load(); }, [load]);

  const { totals, goal, effectiveGoals, autoGoals } = data;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Apple size={20} color="var(--text-primary)" />
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Nutrition</h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 10px' }}>
        What a player logged each day in the Food Tracker. Calories and fat goals are set by
        staff; protein and carb goals are calculated from body weight and the match-day type,
        and can be overridden.
      </p>
      {toggle}

      {/* Controls */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <select value={playerId} onChange={e => setPlayerId(e.target.value)} style={{ ...inputStyle, minWidth: 200 }}>
          <option value="">— select player —</option>
          {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(shiftDate(date, -1))}><ChevronLeft size={15} /></button>
          <input type="date" value={date} onChange={e => setDate(e.target.value || tod())} style={inputStyle} />
          <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(shiftDate(date, 1))}><ChevronRight size={15} /></button>
          <button style={btn(date === tod())} onClick={() => setDate(tod())}>Today</button>
        </div>
      </div>

      {!playerId ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>Select a player.</div>
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Left — day view */}
          <div style={{ flex: '2 1 420px', minWidth: 0 }}>
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Day overview</div>
              <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 12 }}>{fmtDay(date)}</div>
              {loading ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div> : <DayBreakdown entries={data.entries} />}
            </div>

            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Day total vs. goal</div>
              <TotalRow label="Calories" source="manual" value={totals.kcal}      goal={effectiveGoals.kcal_goal}      unit=" kcal" />
              <TotalRow label="Protein"  source={effectiveGoals.protein_is_manual ? 'manual' : 'auto'} value={totals.protein_g} goal={effectiveGoals.protein_g_goal} unit=" g" />
              <TotalRow label="Carbs"    source={effectiveGoals.carbs_is_manual ? 'manual' : 'auto'}   value={totals.carbs_g}   goal={effectiveGoals.carbs_g_goal}   unit=" g" />
              <TotalRow label="Fat"      source="manual" value={totals.fat_g}     goal={effectiveGoals.fat_g_goal}     unit=" g" />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', padding: '0 0 3px 12px' }}>
                <span>— saturated</span><span>{totals.saturated_fat_g ?? 0} g</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', padding: '0 0 2px 12px' }}>
                <span>— unsaturated</span><span>{totals.unsaturated_fat_g ?? 0} g</span>
              </div>
              {!goal && <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8 }}>No calorie / fat goal set for this player yet.</div>}
            </div>
          </div>

          {/* Right — goals */}
          <div style={{ flex: '1 1 300px', minWidth: 280 }}>
            <GoalsEditor playerId={playerId} goal={goal} auto={autoGoals} onSaved={load} user={user} />
            <AutoGoalsPanel auto={autoGoals} />
          </div>
        </div>
      )}

      {playerId && <NutritionProfilePanel playerId={playerId} user={user} />}
    </div>
  );
}

// Top-level Nutrition page. Two very different audiences can land here:
//  - chef role: ONLY ever sees its own weekly recipe planner (editable) — no player picker, no
//    toggle, nothing else. App.js's PageContent already guarantees no other `page` ever reaches
//    a chef account, but this component still branches on role rather than assuming it's only
//    ever mounted for admin, since App.js does route chef here for page==='nutrition'.
//  - admin role: gets a "Spelers"/"Chef" toggle — "Spelers" is the existing player-overview view
//    above, unchanged; "Chef" shows the same weekly planner the chef role uses, but read-only
//    (view-only for now, per the brief — whether admin should also be able to edit it wasn't
//    asked for explicitly, so left as an open question rather than assumed).
// The chef and player nutrition experiences are otherwise completely unaffected by this toggle
// — it only exists on the admin's own screen.
export default function Nutrition({ user }) {
  const [view, setView] = useState('players'); // admin only: 'players' | 'chef'

  if (user.role === 'chef') {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Apple size={20} color="var(--text-primary)" />
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Nutrition — Weekly recipes</h1>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px' }}>
          Plan recipes for each day of the week. Older weeks are kept and stay browsable — nothing gets overwritten when a new week starts.
        </p>
        <ChefWeekPlanner readOnly={false} user={user} />
      </div>
    );
  }

  const toggle = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
      <button style={btn(view === 'players')} onClick={() => setView('players')}>Spelers</button>
      <button style={btn(view === 'chef')} onClick={() => setView('chef')}>Chef</button>
    </div>
  );

  if (view === 'chef') {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Apple size={20} color="var(--text-primary)" />
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Nutrition</h1>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 10px' }}>
          What the chef has planned this week — admin can add and edit recipes here too, same as the chef.
        </p>
        {toggle}
        <ChefWeekPlanner readOnly={false} user={user} />
      </div>
    );
  }

  return <PlayersNutritionView user={user} toggle={toggle} />;
}
